/**
 * Build a persona welcome for a new run (client-side; not persisted as LLM turn).
 */

export function buildPersonaWelcome(input: {
  agentName: string;
  persona: string | null;
  purpose: string | null;
  hasPriorConfig: boolean;
  /** Short plain summary of prior config when present. */
  priorSummary?: string | null;
}): string {
  const lines: string[] = [];
  lines.push(`Hi — I'm ${input.agentName}.`);
  if (input.persona?.trim()) {
    lines.push(input.persona.trim());
  }
  if (input.purpose?.trim()) {
    lines.push(`I can help you with: ${input.purpose.trim()}`);
  } else {
    lines.push('Tell me what you want to work on and I’ll help within my role.');
  }
  lines.push('');
  if (input.hasPriorConfig) {
    lines.push(
      input.priorSummary?.trim()
        ? `Here is the latest configuration I have: ${input.priorSummary.trim()}`
        : 'Here is the latest configuration I have from your previous work with me.'
    );
    lines.push('How would you like to change it? Or tell me what you want to figure out today.');
  } else {
    lines.push(
      "I don't have saved setup for you on this run yet. Tell me what you're trying to do and I'll ask for what I need."
    );
  }
  return lines.join('\n');
}

/** Compact working-set blurb for welcome (no jargon labels). */
export function summarizeWorkingSet(workingSet: Record<string, unknown>): string | null {
  const quality =
    workingSet.quality && typeof workingSet.quality === 'object' && !Array.isArray(workingSet.quality)
      ? (workingSet.quality as Record<string, unknown>)
      : null;
  if (quality && Object.keys(quality).length > 0) {
    const parts: string[] = [];
    if (typeof quality.lineId === 'string' && quality.lineId.trim()) {
      parts.push(`line ${quality.lineId.trim()}`);
    }
    if (typeof quality.partNumber === 'string' && quality.partNumber.trim()) {
      parts.push(`part ${quality.partNumber.trim()}`);
    }
    const events = Array.isArray(quality.recentEvents) ? quality.recentEvents.length : 0;
    const ncrs = Array.isArray(quality.openNcrs) ? quality.openNcrs.length : 0;
    if (events) parts.push(`${events} recent quality events`);
    if (ncrs) parts.push(`${ncrs} open NCRs`);
    if (parts.length) return parts.join(', ');
  }

  const resources = Array.isArray(workingSet.resources) ? workingSet.resources.length : 0;
  const demands = Array.isArray(workingSet.demands) ? workingSet.demands.length : 0;
  const tasks = Array.isArray(workingSet.tasks) ? workingSet.tasks.length : 0;
  if (resources + demands + tasks === 0) return null;
  const parts: string[] = [];
  if (resources) parts.push(`${resources} resources`);
  if (demands) parts.push(`${demands} orders`);
  if (tasks) parts.push(`${tasks} jobs`);
  const disruption =
    typeof workingSet.disruption === 'string' && workingSet.disruption.trim()
      ? workingSet.disruption.trim()
      : null;
  let summary = parts.join(', ');
  if (disruption) summary += `. Note: ${disruption}`;
  return summary;
}
